import { Point } from "geojson";
import { Dispatch } from "react";
import * as H from "history";
import { SSN } from "../components/SSNInput";
import { CoverageType } from "../pages/QuestionRoutingPage/types";

declare global {
  interface Window {
    utag?: {
      link: (params: Record<string, unknown>) => void;
      view: (params: Record<string, unknown>) => void;
    };
  }
}

export enum UserRole {
  ANONYMOUS = "anon",
  BENEFICIARY = "beneficiary",
  CSR = "csr",
}

export interface PharmaciesRequest {
  zipcode: string;
  address?: string;
  latitude?: string;
  longitude?: string;
  name?: string;
  radius_miles?: number;
  count: number;
  page: number;
  preferred_only?: boolean;
}

export interface AbstractBenefitFrequencyCost {
  days_30: string | null;
  days_90: string | null;
  days_any: string | null;
}

export interface AbstractBenefitPhase {
  footnote: string;
  tiers: AbstractBenefitTier[];
}

export enum AbstractBenefitPhaseEnum {
  initial_coverage = "initial_coverage",
  coverage_gap = "coverage_gap",
  catastrophic = "catastrophic",
  lis = "lis",
  platino = "platino",
}

export interface AbstractBenefits {
  catastrophic: AbstractBenefitPhase | null;
  coverage_gap: AbstractBenefitPhase | null;
  initial_coverage: AbstractBenefitPhase | null;
  lis: AbstractBenefitPhase | null;
  platino: AbstractBenefitPhase | null;
}

export interface AbstractBenefitTier {
  general: string | null;
  label: string;
  preferred_mail_order: AbstractBenefitFrequencyCost | null;
  preferred_retail: AbstractBenefitFrequencyCost | null;
  standard_mail_order: AbstractBenefitFrequencyCost | null;
  standard_retail: AbstractBenefitFrequencyCost | null;
}

export enum AccessibilityFormat {
  DEFAULT = "",
  BRAILLE = "Braille",
  LARGE_PRINT = "LargePrint",
  AUDIO_CD = "AudioCD",
}

export interface Action {
  payload?: unknown;
  type: ActionType;
}

export enum ActionType {
  ADD_BENEFICIARY = "ADD_BENEFICIARY",
  ADD_CSR = "ADD_CSR",
  ADD_CURRENT_COVERAGE = "ADD_CURRENT_COVERAGE",
  ADD_NEXT_YEAR_COVERAGE = "ADD_NEXT_YEAR_COVERAGE",
  ADD_PHARMACY = "ADD_PHARMACY",
  ADD_PRESCRIPTION = "ADD_PRESCRIPTION",
  DELETE_PHARMACY = "DELETE_PHARMACY",
  DELETE_PRESCRIPTION = "DELETE_PRESCRIPTION",
  EDIT_PRESCRIPTION = "EDIT_PRESCRIPTION",
  RESET_PHARMACIES = "RESET_PHARMACIES",
  RESET_STATE = "RESET_STATE",
  SAVE_PHARMACIES = "SAVE_PHARMACIES",
  SET_PHARMACIES = "SET_PHARMACIES",
  SET_PRESCRIPTIONS = "SET_PRESCRIPTIONS",
  SET_TEALIUM_LOADED = "SET_TEALIUM_LOADED",
  UPDATE_COMPARE_LIST = "UPDATE_COMPARE_LIST",
  UPDATE_COUNTY = "UPDATE_COUNTY",
  UPDATE_CW_COVERAGE = "UPDATE_CW_COVERAGE",
  UPDATE_DEGRADED_INTEGRATIONS = "UPDATE_DEGRADED_INTEGRATIONS",
  UPDATE_DEMOGRAPHICS = "UPDATE_DEMOGRAPHICS",
  UPDATE_FIPS = "UPDATE_FIPS",
  UPDATE_FUTURE_LIS = "UPDATE_FUTURE_LIS",
  UPDATE_LANGUAGE = "UPDATE_LANGUAGE",
  UPDATE_LAST_ACTIVITY = "UPDATE_LAST_ACTIVITY",
  UPDATE_LIS = "UPDATE_LIS",
  UPDATE_MBP_HANDOFF_FAILED = "UPDATE_MBP_HANDOFF_FAILED",
  UPDATE_PHARMACY_ADDRESS = "UPDATE_PHARMACY_ZIP",
  UPDATE_PHARMACY_NETWORK_STATUS = "UPDATE_PHARMACY_NETWORK_STATUS",
  UPDATE_PHARMACY_TYPE = "UPDATE_PHARMACY_TYPE",
  UPDATE_PLANTYPE = "UPDATE_PLAN_TYPE",
  UPDATE_SEARCH_RESULTS_FILTERS = "UPDATE_SEARCH_RESULTS_FILTERS",
  UPDATE_SEARCH_RESULTS_FILTERS_CARRIERS = "UPDATE_SEARCH_RESULTS_FILTERS_CARRIERS",
  UPDATE_SELECTED_PLAN = "UPDATE_SELECTED_PLAN",
  UPDATE_SNP_FILTERS = "UPDATE_SNP_FILTERS",
  UPDATE_VIEW_MEDIGAP = "VIEW_MEDIGAP",
  UPDATE_YEAR = "UPDATE_YEAR",
  UPDATE_ZIPCODE = "UPDATE_ZIPCODE",
  UPDATE_MBP_REFERRER_URI = "UPDATE_MBP_REFERRER_URI",
}

export interface AdditionalSupplementalBenefits {
  mmp_benefits: OtherBenefitCategory[];
  other_benefits: OtherBenefitCategory[];
  special_benefits: OtherBenefitCategory[];
}

export enum AdditionalSupplementalBenefitCoverage {
  SB_COVERAGE_NOT_COVERED = "SB_COVERAGE_NOT_COVERED",
  SB_COVERAGE_SOME_COVERAGE = "SB_COVERAGE_SOME_COVERAGE",
}

export interface ApiErrorMessage {
  errorResponse: ApiErrorResponse;
  status: number;
  statusText: string;
  type: string;
  url: string;
}

export interface ApiErrorResponse {
  code?: number;
  details?: ApiErrorResponseDetail[];
  error: string;
  message?: string;
}

export interface ApiErrorResponseDetail {
  "@type": string;
  request_id: string;
  serving_data: ApiResponseServingData | string;
}

export interface CwPremiumRanges {
  request_id: string;
  pdp_premium_min: number | null;
  pdp_premium_max: number | null;
  ma_premium_min: number | null;
  ma_premium_max: number | null;
  medigap_premium_min: number | null;
  medigap_premium_max: number | null;
}

interface ApiResponseQueryObject {
  contract_id: string;
  plan_id: string;
  segment_id: string;
  year: string;
}

export interface ApiResponseServingData {
  query?: ApiResponseQueryObject;
}

export interface AppContextType {
  dispatch: Dispatch<Action>;
  state: AppState;
}
export interface AppState {
  beneficiary?: Beneficiary;
  compareList: SearchResultPlan[];
  county?: County;
  csr?: CSR;
  currentCoverage?: SearchResultPlan;
  degradedIntegrations: string[];
  fips?: string;
  language: UserLanguage;
  lastActivity?: Date;
  lis?: LowIncomeSubsidyStatus;
  futureLis?: LowIncomeSubsidyStatus;
  mbpHandoffFailed: boolean;
  mbpReferrerUri: string;
  nextYearCoverage?: SearchResultPlan;
  pharmacies: Pharmacy[];
  pharmacyAddress?: string;
  pharmacyType: PharmacyType;
  planType?: PlanType;
  prescriptions: PrescriptionDrug[];
  searchResultsFilters: SearchResultsFilters;
  selectedPlan?: Plan | SearchResultPlan;
  snpFilters?: PlanSNPType[];
  tealiumLoaded?: boolean;
  viewMedigap: boolean;
  year?: string;
  zipcode?: string;
  demographicInfo?: DemographicInfo;
  cwCoverage?: CoverageType;
}

export interface AutocompleteDrug {
  generic: { name: string; rxcui: string } | null;
  is_generic: boolean;
  name: string;
  rxcui: string;
}

export interface AutocompletePapDrug {
  name: string;
  id: string;
}

export interface AutocompleteDrugResults {
  drugs: AutocompleteDrug[];
}

export interface BasePlan {
  readonly annual_deductible: string | number;
  readonly calculated_monthly_premium: number;
  readonly category: PlanCategoryType;
  readonly coinsurance_amount_preferred: string;
  readonly contract_id: string;
  readonly contract_year: string;
  readonly copay_amount_preferred: string;
  readonly drug_plan_deductible: number;
  readonly enrollment_opt_in_status: boolean;
  readonly enrollment_status: PlanEnrollmentStatus;
  readonly excluded: boolean;
  readonly high_performing: boolean;
  readonly id: number;
  readonly lis: LIS;
  readonly low_performing: boolean;
  readonly monthly_premium: number;
  readonly msa_annual_deposit: number;
  readonly name_esp: string;
  /** Name of plan, access with getPlanName() helper */
  readonly name: string;
  readonly organization_name: string;
  readonly overall_star_rating: StarRating;
  readonly package_services: PackageServices | null;
  readonly partb_premium_reduction: number;
  readonly partc_premium: number;
  readonly partd_premium: number;
  readonly plan_id: string;
  readonly plan_type: PlanType;
  readonly redactions: Redaction[];
  readonly segment_id: string;
  readonly silver_sneakers: boolean;
  readonly snp_type: PlanSNPType;
  readonly total_remaining_premium: number;
  readonly url: string;
  readonly worldwide_emergency: boolean;
}

export enum NewToMedicareStatus {
  NEW_TO_MEDICARE_UNDEFINED = "NEW_TO_MEDICARE_UNDEFINED",
  NEW_TO_MEDICARE_YES = "NEW_TO_MEDICARE_YES",
  NEW_TO_MEDICARE_NO = "NEW_TO_MEDICARE_NO",
}
export interface Beneficiary {
  activity_id: string;
  birthdate: string;
  csr_id: string;
  current_coverage: ExtendedCoverageInfo[];
  email: string;
  firstname: string[];
  future_coverage: ExtendedCoverageInfo[];
  gender: Gender;
  hicn: string;
  lastname: string;
  lis_level: LowIncomeSubsidyStatus;
  future_lis_level: LowIncomeSubsidyStatus;
  future_lis_status_start_date: string;
  mailing_address: {
    address_1: string;
    address_2: string;
    address_3: string;
    city: string;
    county: string;
    state: string;
    zipcode: string;
  };
  mbi: string;
  part_a_start: string;
  part_b_start: string;
  pdes: PDE[];
  plans: string[];
  request_id: string;
  ssn: string;
  new_to_medicare: NewToMedicareStatus;
}

export interface BeneficiaryAndApiHeaders {
  beneficiary: Beneficiary | undefined;
  headers: Headers;
}

export interface BenefitItemProps {
  benefit: string;
  label: string | JSX.Element;
  mobileLabelOnly?: boolean;
  plan: Plan;
  service?: string;
  suppressed?: boolean;
}

export interface CallbackOutput {
  code: string;
  state: number;
}

export interface ChatInit {
  ActivityId: string;
  EmailAddress: string;
  FirstName: string;
  LastName: string;
  PhoneNumber: string;
  Language: string;
  CurrentView: string; // mct-<current-page-name>
}

export interface ClientErrorMessage {
  apiError?: string;
  cause: string;
}

export interface CSR {
  activityId: string;
  id: string;
}

export interface County {
  fips: string;
  name: string;
  state: string;
}

export interface CoverageInfo extends IdParts {
  coverage_year?: string;
}

export interface DemographicInfo {
  age?: string;
  gender?: Gender;
  tobacco?: boolean;
}

export interface DrugDosage {
  form: string;
  has_duplicate_name: boolean;
  name: string;
  packages: DrugPackage[];
  rxcui: string;
  strength_units: string;
  strength: string;
}

export interface DrugDosageInfo {
  commonDosage: DrugDosage | null;
  dosages: DrugDosage[];
}

export interface DrugCost {
  catastrophic_cost: number | string;
  covered: boolean;
  deductible_cost: number | string;
  frequency: PrescriptionFrequency;
  full_cost: number | string;
  gap_cost: number | string;
  initial_cost: number | string;
  ndc: string;
  quantity: number | string;
  default_price_used: boolean;
  coverage_reason: DrugCostCoverageReason;
  estimated_yearly_total: number;
}

export interface DrugCostInfo {
  costs: DrugCosts[];
  excluded_drugs: string[];
  lowest_mail_total: number | string;
  lowest_retail_total: number | string;
  plan: FullPlanId;
  restrictions: {
    conditions: string[];
    free_first_fill: boolean;
    home_infusion: boolean;
    limited_distribution: boolean;
    ndc: string;
    opioid: boolean;
    partial_gap: boolean;
    vaccine: boolean;
  }[];
}

export enum DrugCostCoverageReason {
  COVERED = "COVERED",
  OUT_OF_NETWORK = "OUT_OF_NETWORK",
  NOT_IN_FORMULARY = "NOT_IN_FORMULARY",
  FREQUENCY_NOT_COVERED = "FREQUENCY_NOT_COVERED",
}

export interface DrugCosts {
  drug_costs: DrugCost[];
  estimated_monthly_costs: EstimatedMonthlyDrugCost[];
  estimated_yearly_total: number;
  in_network: boolean;
  mail_order: boolean;
  npi: string;
  phase_information: DrugPhaseInformation;
  preferred: boolean;
  ltc: boolean;
}

// response from /drugs/cost API
export interface DrugCostsResponse {
  plans: DrugCostInfo[];
  request_id: string;
}

export enum DrugCoverageFilterOption {
  INCLUDES = "includes",
  EXCLUDES = "excludes",
}

export enum DrugFootnote {
  CONDITION = "condition",
  EXCLUDED_DRUG = "excluded_drug",
  FREE_FIRST_FILL = "free_first_fill",
  HOME_INFUSION = "home_infusion",
  LIMITED_DISTRIBUTION = "limited_distribution",
  OPIOID = "opioid",
  PARTIAL_GAP = "partial_gap",
  VACCINE = "vaccine",
  NOT_IN_FORMULARY = "not_in_formulary",
  DEFAULT_PRICE = "default_price_used",
  FREQUENCY_NOT_COVERED = "frequency_not_covered",
}

export interface DrugInfo {
  ndc: string;
  on_formulary?: boolean;
  prior_auth: boolean;
  quantity_limit_amount: number;
  quantity_limit_days: number;
  quantity_limit: boolean;
  step_therapy: boolean;
  tier: number;
}

export interface DrugPackage {
  default_quantity_30: number;
  default_quantity_90: number;
  ndc: string;
  quantity: number;
  size: number;
  type: string;
  unit: DrugPackageUnit;
  use_quantity: boolean;
  volume: number;
}

export enum DrugPackageUnit {
  "N/A" = "N/A",
  EA = "EA",
  GM = "GM",
  ML = "ML",
}

export interface DrugPhaseInformation {
  catastrophic_start: string;
  deductible_amount: number;
  gap_start: string;
  initial_coverage_start: string;
}

export enum EffectiveSubsidyLevel {
  EFFECTIVE_SUBSIDY_LEVEL_FULL_DUAL = "EFFECTIVE_SUBSIDY_LEVEL_FULL_DUAL",
  EFFECTIVE_SUBSIDY_LEVEL_FULL_SUBSIDY = "EFFECTIVE_SUBSIDY_LEVEL_FULL_SUBSIDY",
  EFFECTIVE_SUBSIDY_LEVEL_NO_SUBSIDY = "EFFECTIVE_SUBSIDY_LEVEL_NO_SUBSIDY",
  EFFECTIVE_SUBSIDY_LEVEL_PARTIAL_DUAL = "EFFECTIVE_SUBSIDY_LEVEL_PARTIAL_DUAL",
  EFFECTIVE_SUBSIDY_LEVEL_PARTIAL_SUBSIDY = "EFFECTIVE_SUBSIDY_LEVEL_PARTIAL_SUBSIDY",
}

export enum EffectiveSubsidyPercent {
  EFFECTIVE_SUBSIDY_PERCENT_100 = "EFFECTIVE_SUBSIDY_PERCENT_100",
  EFFECTIVE_SUBSIDY_PERCENT_25 = "EFFECTIVE_SUBSIDY_PERCENT_25",
  EFFECTIVE_SUBSIDY_PERCENT_50 = "EFFECTIVE_SUBSIDY_PERCENT_50",
  EFFECTIVE_SUBSIDY_PERCENT_75 = "EFFECTIVE_SUBSIDY_PERCENT_75",
}

export interface EnrollData {
  additionalDrugCoverage: {
    group: string;
    id: string;
    name: string;
  };
  address1: string;
  address2: string;
  agreeToContract?: boolean;
  agreeToInformationRelease?: boolean;
  authorizedRepresentative: {
    name: string;
    relationship: string;
    addressLine1: string;
    city: string;
    state: string;
    zip: string;
    phone: EnrollPhoneNumber;
  };
  city: string;
  confirmationNumber?: string;
  county: County | undefined;
  differentMailingAddress?: boolean;
  dob: MCTDate;
  email: string;
  emergencyContactName: string;
  emergencyContactPhone: EnrollPhoneNumber;
  emergencyContactRelationship: string;
  employerHealthCoverage?: boolean;
  enrolledInMedicaid?: boolean;
  firstName: string;
  hasAdditionalDrugCoverage?: boolean;
  hasSepOtherReason: boolean;
  inLongTermCareFacility?: boolean;
  isEnrollee?: boolean;
  lastName: string;
  longTermCareFacility: {
    addressLine1: string;
    city: string;
    name: string;
    phone: EnrollPhoneNumber;
    state: string;
    zip: string;
  };
  mailingAddress1: string;
  mailingAddress2: string;
  mailingCity: string;
  mailingState: string;
  mailingZip: string;
  medicareNumber: string;
  middleInitial: string;
  partAStart: MCTDate;
  partBStart: MCTDate;
  premiumDeducted?: boolean;
  premiumDirectPay?: boolean;
  premiumWithhold: string;
  primaryCareName: string;
  primaryPhone: EnrollPhoneNumber;
  provideEmergencyContact?: boolean;
  readAndUnderstoodContents?: boolean;
  renalDisease?: boolean;
  sepOtherReason: string;
  sepReasons: SepReason[];
  sex: Sex;
  ssn: SSN;
  state: string;
  title: Title;
  understoodCoverageLossWarning?: boolean;
  zip: string;
}

export interface EnrollData2021 {
  accessibilityFormat?: AccessibilityFormat;
  additionalDrugCoverage: {
    group: string;
    id: string;
    name: string;
  };
  address1: string;
  address2: string;
  agreeToContract?: boolean;
  agreeToInformationRelease?: boolean;
  authorizedRepresentative: {
    name: string;
    relationship: string;
    addressLine1: string;
    city: string;
    state: string;
    zip: string;
    phone: EnrollPhoneNumber;
  };
  beneWorks?: boolean;
  city: string;
  confirmationNumber?: string;
  county: County | undefined;
  differentMailingAddress?: boolean;
  dob: MCTDate;
  email: string;
  emailOptIn?: boolean;
  firstName: string;
  hasAdditionalDrugCoverage?: boolean;
  hasSepOtherReason: boolean;
  isEnrollee?: boolean;
  language?: PlanMaterialLanguage;
  lastName: string;
  mailingAddress1: string;
  mailingAddress2: string;
  mailingCity: string;
  mailingState: string;
  mailingZip: string;
  medicareNumber: string;
  middleInitial: string;
  premiumDeducted?: boolean;
  premiumDirectPay?: boolean;
  premiumWithhold: string;
  primaryCareName: string;
  primaryPhone: EnrollPhoneNumber;
  readAndUnderstoodContents?: boolean;
  sepOtherReason: string;
  sepReasons: SepReason[];
  sex: Sex;
  spouseWorks?: boolean;
  ssn: SSN;
  state: string;
  understoodCoverageLossWarning?: boolean;
  zip: string;
}

// For 2022, ethnicities and races have been added to the API (/beneinfo/oec-enroll)
// add them when they appear on enrollment form.
export interface EnrollData2022 {
  accessibilityFormat?: AccessibilityFormat;
  additionalDrugCoverage: {
    group: string;
    id: string;
    name: string;
  };
  address1: string;
  address2: string;
  agreeToContract?: boolean;
  agreeToInformationRelease?: boolean;
  authorizedRepresentative: {
    name: string;
    relationship: string;
    addressLine1: string;
    city: string;
    state: string;
    zip: string;
    phone: EnrollPhoneNumber;
  };
  beneWorks?: boolean;
  city: string;
  confirmationNumber?: string;
  county: County | undefined;
  differentMailingAddress?: boolean;
  dob: MCTDate;
  email: string;
  emailOptIn?: boolean;
  firstName: string;
  hasAdditionalDrugCoverage?: boolean;
  hasSepOtherReason: boolean;
  isEnrollee?: boolean;
  language?: PlanMaterialLanguage;
  lastName: string;
  mailingAddress1: string;
  mailingAddress2: string;
  mailingCity: string;
  mailingState: string;
  mailingZip: string;
  medicareNumber: string;
  middleInitial: string;
  premiumDeducted?: boolean;
  premiumDirectPay?: boolean;
  premiumWithhold: string;
  primaryCareName: string;
  primaryPhone: EnrollPhoneNumber;
  readAndUnderstoodContents?: boolean;
  sepOtherReason: string;
  sepReasons: SepReason[];
  sex: Sex;
  spouseWorks?: boolean;
  ssn: SSN;
  state: string;
  understoodCoverageLossWarning?: boolean;
  zip: string;
}

export interface EnrollDataForAPI {
  agent_id: string | null;
  applicant_info: {
    birthdate: string;
    email_address: string;
    emergency_contact_name: string;
    emergency_contact_phone: string;
    emergency_contact_relation: string;
    first_name: string;
    gender: string;
    hicn: string;
    home_address: {
      address_1: string;
      address_2: string;
      city: string;
      state: string;
      zipcode: string;
      county: string;
    };
    last_name: string;
    mail_address: {
      address_1: string;
      address_2: string;
      city: string;
      state: string;
      zipcode: string;
    };
    middle_initial: string;
    part_a_start: string;
    part_b_start: string;
    phone: string;
    ssn: string;
    title: string;
  };
  authorized_rep: {
    name: string;
    address: {
      address_1: string;
      address_2: string;
      city: string;
      state: string;
      zipcode: string;
    };
    phone: string;
    relation: string;
  } | null;
  contract_id: string;
  esrd: boolean;
  lis_level: string | undefined;
  long_term: {
    address: string;
    name: string;
    phone: string;
  } | null;
  other_coverage: {
    group: string;
    id: string;
    name: string;
  } | null;
  part_d_optout: boolean;
  plan_id: string;
  plan_year: string;
  premium_direct_pay: boolean;
  premium_withhold: string;
  primary_care_physician: string;
  segment_id: string;
  sep_cms_reason_code: string | null;
  sep_reason_codes: SepReasonForApi[] | null;
  state_medicaid: boolean;
  work_status: boolean;
}

export interface EnrollData2021ForAPI {
  accessibility_format: AccessibilityFormat | null;
  agent_id: string | null;
  applicant_info: {
    birthdate: string;
    email_address: string;
    first_name: string;
    gender: string;
    hicn: string;
    home_address: {
      address_1: string;
      address_2: string;
      city: string;
      state: string;
      zipcode: string;
      county: string;
    };
    language: PlanMaterialLanguage | null;
    last_name: string;
    mail_address: {
      address_1: string;
      address_2: string;
      city: string;
      state: string;
      zipcode: string;
    };
    middle_initial: string;
    phone: string;
    ssn: string;
  };
  authorized_rep: {
    name: string;
    address: {
      address_1: string;
      address_2: string;
      city: string;
      state: string;
      zipcode: string;
    };
    phone: string;
    relation: string;
  } | null;
  contract_id: string;
  email_opt_in: boolean;
  lis_level: string | undefined;
  other_coverage: {
    group: string;
    id: string;
    name: string;
  } | null;
  plan_id: string;
  plan_year: string;
  premium_direct_pay: boolean;
  premium_withhold: string;
  primary_care_physician: string;
  segment_id: string;
  sep_cms_reason_code: string | null;
  sep_reason_codes: SepReasonForApi[] | null;
  spouse_work_status: boolean | null;
  work_status: boolean | null;
}

// For 2022, ethnicities and races have been added to the API (/beneinfo/oec-enroll)
// add them when they appear on enrollment form.
export interface EnrollData2022ForAPI {
  accessibility_format: AccessibilityFormat | null;
  agent_id: string | null;
  applicant_info: {
    birthdate: string;
    email_address: string;
    first_name: string;
    gender: string;
    mbi: string;
    home_address: {
      address_1: string;
      address_2: string;
      city: string;
      state: string;
      zipcode: string;
      county: string;
    };
    language: PlanMaterialLanguage | null;
    last_name: string;
    mail_address: {
      address_1: string;
      address_2: string;
      city: string;
      state: string;
      zipcode: string;
    };
    middle_initial: string;
    phone: string;
    ssn: string;
  };
  authorized_rep: {
    name: string;
    address: {
      address_1: string;
      address_2: string;
      city: string;
      state: string;
      zipcode: string;
    };
    phone: string;
    relation: string;
  } | null;
  contract_id: string;
  email_opt_in: boolean;
  lis_level: string | undefined;
  other_coverage: {
    group: string;
    id: string;
    name: string;
  } | null;
  plan_id: string;
  plan_year: string;
  premium_direct_pay: boolean;
  premium_withhold: string;
  primary_care_physician: string;
  segment_id: string;
  sep_cms_reason_code: string | null;
  sep_reason_codes: SepReasonForApi[] | null;
  spouse_work_status: boolean | null;
  work_status: boolean | null;
}

export enum EnrollFormPlanTypeLabels {
  PLAN_CATEGORY_1876_COST_MA = "enroll_form.cp_ma_type.label",
  PLAN_CATEGORY_1876_COST_PD = "enroll_form.cp_pd_type.label",
  PLAN_CATEGORY_PFFS_MA = "enroll_form.pffs_ma_type.label",
  PLAN_CATEGORY_PFFS_PD = "enroll_form.pffs_pd_type.label",
  PLAN_TYPE_MA = "enroll_form.ma_type.label",
  PLAN_TYPE_MAPD = "enroll_form.mapd_type.label",
  PLAN_TYPE_PDP = "enroll_form.pdp_type.label",
  SNP_TYPE_DUAL_ELIGIBLE = "enroll_form.snp_de_type.label",
}

export enum EnrollFormSNPTypeLabelKeys {
  SNP_TYPE_CHRONIC_OR_DISABLING = "enroll_form.snp_cc_type.label",
  SNP_TYPE_DUAL_ELIGIBLE = "enroll_form.snp_de_type.label",
  SNP_TYPE_INSTITUTIONAL = "enroll_form.snp_i_type.label",
}

export interface EnrollPhoneNumber {
  areaCode: string;
  exchangeCode: string;
  lineNumber: string;
}

export interface EnrollResponse {
  confirmation_number: string;
  status: EnrollResponseCode;
}

export enum EnrollResponseCode {
  OEC_BAD_CONTRACT_ID = "OEC_BAD_CONTRACT_ID",
  OEC_BAD_PLAN_ID = "OEC_BAD_PLAN_ID",
  OEC_BAD_SEGMENT_ID = "OEC_BAD_SEGMENT_ID",
  OEC_DUPLICATE_ENROLLMENT = "OEC_DUPLICATE_ENROLLMENT",
  OEC_FIELD_WRONG_DATA_TYPE = "OEC_FIELD_WRONG_DATA_TYPE",
  OEC_INCORRECT_NUMBER_OF_FIELDS = "OEC_INCORRECT_NUMBER_OF_FIELDS",
  OEC_UNKNOWN = "OEC_UNKNOWN",
  OEC_UNKNOWN_ERROR = "OEC_UNKNOWN_ERROR",
  OEC_VALID = "OEC_VALID",
}

export enum Envs {
  imp = "imp",
  local = "local",
  prod = "prod",
  test = "test",
}

export interface EstimatedMonthlyDrugCost {
  catastrophic: number;
  date: string;
  deductible: number;
  gap: number;
  initial: number;
  total: number;
}

export interface ExtendedCoverageInfo extends IdParts {
  coverage_year: string;
  name: string;
  name_esp: string;
  enrollment_effective_date: string;
  contract_effective_date: string;
  disenrollment_date: string;
}

export interface FullPlanId extends CoverageInfo {
  contract_year: string;
}

export enum Gender {
  FEMALE = "GENDER_FEMALE",
  MALE = "GENDER_MALE",
  UNKNOWN = "GENDER_NA",
}

export interface IdParts {
  contract_id: string;
  contract_year?: string;
  coverage_year?: string;
  plan_id: string;
  segment_id: string;
}

export interface Indexable {
  index: number;
}

export interface LIS {
  readonly level_100: number;
  readonly level_25: number;
  readonly level_50: number;
  readonly level_75: number;
}

export enum LowIncomeSubsidyAmount {
  Help0 = "LEVEL_0",
  Help25 = "LEVEL_25",
  Help50 = "LEVEL_50",
  Help75 = "LEVEL_75",
  HelpNA = "LEVEL_NA",
  HelpUnsure = "LEVEL_100",
}

export enum LowIncomeSubsidyOption {
  Medicaid = "LIS_MEDICAID",
  MSP = "LIS_MSP",
  None = "LIS_NONE",
  SS = "LIS_SS",
  SSI = "LIS_SSI",
  Unsure = "LIS_UNSURE",
}

export enum LowIncomeSubsidyStatus {
  LIS_LEVEL_1A = "LIS_LEVEL_1A",
  LIS_LEVEL_1A_DN = "LIS_LEVEL_1A_DN",
  LIS_LEVEL_1B = "LIS_LEVEL_1B",
  LIS_LEVEL_1C = "LIS_LEVEL_1C",
  LIS_LEVEL_2 = "LIS_LEVEL_2",
  LIS_LEVEL_3 = "LIS_LEVEL_3",
  LIS_LEVEL_4_100 = "LIS_LEVEL_4_100",
  LIS_LEVEL_4_100H = "LIS_LEVEL_4_100H",
  LIS_LEVEL_4_25 = "LIS_LEVEL_4_25",
  LIS_LEVEL_4_50 = "LIS_LEVEL_4_50",
  LIS_LEVEL_4_75 = "LIS_LEVEL_4_75",
  LIS_NO_HELP = "LIS_NO_HELP",
}

export interface MCTDate {
  day: string;
  month: string;
  year: string;
}

export enum MctEvent {
  costsLoaded = "costs_loaded",
}

export enum MctToastType {
  BENE_FAILED_LOGIN = "bfl",
  CSR_BENE_FAIL = "cbf",
  LOGGED_OUT = "lo",
  SESSION_EXPIRED = "se",
}

export interface MenuItem {
  id?: string;
  onClick?: () => void;
  title: string;
  url?: string;
}

export interface NetworkCostDescriptor {
  readonly authorization_required: boolean;
  readonly cost_share: string;
  readonly plan_limits: boolean;
  readonly referral_required: boolean;
}

export interface NetworkCostDescriptors {
  /* "NETWORK_TYPE_IN_NETWORK" */
  readonly [x: string]: NetworkCostDescriptor;
}

export interface OecStatus {
  confirmation_number: string;
  contract_id: string;
  download_date: string;
  downloaded: boolean;
  plan_id: string;
  plan_name_esp: string;
  plan_name: string;
  segment_id: string;
  year: string;
}

export interface OecStatusResponse {
  statuses: OecStatus[];
}

export interface OOPCCategoryValues {
  readonly acute_heart_condition_drugs: number;
  readonly acute_heart_condition: number;
  readonly all_other_services: number;
  readonly annual_breakdown_total: number;
  readonly chronic_heart_failure_drugs: number;
  readonly chronic_heart_failure: number;
  readonly dental_services_cost: number;
  readonly diabetes_drugs: number;
  readonly diabetes: number;
  readonly inpatient_care: number;
  readonly monthly_breakdown_total: number;
  readonly part_d_drugs: number;
  readonly partb_premium: number;
  readonly partc_premium: number;
  readonly partd_premium: number;
}

export interface OOPCCategories {
  readonly [level: string]: OOPCCategoryValues;
}

export interface OptionalBenefitPackage {
  benefits: string[];
  readonly deductible: string;
  readonly monthly_premium: string;
  readonly package_number: number;
}

export interface OtherBenefit {
  benefit: string;
  coverage: string;
  info?: {
    authorization_required: boolean;
    coins_max_pct: number | null;
    coins_min_pct: number | null;
    copay_max: number | null;
    copay_min: number | null;
    oon_coins_max_pct: number | null;
    oon_coins_min_pct: number | null;
    oon_copay_max: number | null;
    oon_copay_min: number | null;
    referral_required: boolean;
  };
}

export interface OtherBenefitCategory {
  benefits: OtherBenefit[];
  category: string;
}

export enum PaceDay {
  DAY_SUNDAY = "DAY_SUNDAY",
  DAY_MONDAY = "DAY_MONDAY",
  DAY_TUESDAY = "DAY_TUESDAY",
  DAY_WEDNESDAY = "DAY_WEDNESDAY",
  DAY_THURSDAY = "DAY_THURSDAY",
  DAY_FRIDAY = "DAY_FRIDAY",
  DAY_SATURDAY = "DAY_SATURDAY",
}

export interface PaceOfficeHours {
  close_time: string;
  day: PaceDay;
  open: boolean;
  open_all_day: boolean;
  open_time: string;
  time_zone: PaceTimeZone;
}

export interface PacePhone {
  number: string;
  extension: string;
}

export interface PacePhones {
  local_phone: PacePhone | null;
  local_tty: PacePhone | null;
}

export interface PacePlan {
  contract_id: string;
  contract_year: string;
  office_hours: PaceOfficeHours[];
  organization_name: string;
  plan_id: string;
  plan_name_esp: string;
  plan_name: string;
  prospect_phones: PacePhones;
  segment_id: string;
  state: string;
  website: string;
}

export enum PaceTimeZone {
  TIME_ZONE_ALASKA = "TIME_ZONE_ALASKA",
  TIME_ZONE_CENTRAL = "TIME_ZONE_CENTRAL",
  TIME_ZONE_EASTERN = "TIME_ZONE_EASTERN",
  TIME_ZONE_HAWAII = "TIME_ZONE_HAWAII",
  TIME_ZONE_MOUNTAIN = "TIME_ZONE_MOUNTAIN",
  TIME_ZONE_PACIFIC = "TIME_ZONE_PACIFIC",
  TIME_ZONE_SAMOA = "TIME_ZONE_SAMOA",
}

export interface PapPlan {
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  phone1: string;
  phone2: string;
  url: string;
  program_name: string;
  org_name: string;
  eligibility: string[];
  eligibility_spanish: string[];
}

export interface SpapPlan {
  phone1: string;
  program_name: string;
  state: string;
  url: string;
  restrictions: {
    income: boolean;
    age: boolean;
    asset: boolean;
    lis: boolean;
    part_d_enrollment: boolean;
    limited_pharmacies: boolean;
    other_factors: boolean;
  };
}

export enum SupplementalBenefitCategoryTypeNames {
  SB_CAT_WORLDWIDE_EMERGENCY_URGENT_COVERAGE = "SB_CAT_WORLDWIDE_EMERGENCY_URGENT_COVERAGE",
  SB_CAT_CHIROPRACTIC_SERVICES = "SB_CAT_CHIROPRACTIC_SERVICES",
  SB_CAT_ADDITIONAL_TELEHEALTH_SERVICES = "SB_CAT_ADDITIONAL_TELEHEALTH_SERVICES",
  SB_CAT_OPIOID_TREATMENT_SERVICES = "SB_CAT_OPIOID_TREATMENT_SERVICES",
  SB_CAT_TRANSPORTATION_SERVICES = "SB_CAT_TRANSPORTATION_SERVICES",
  SB_CAT_OTC_ITEMS = "SB_CAT_OTC_ITEMS",
  SB_CAT_MEAL_BENEFIT = "SB_CAT_MEAL_BENEFIT",
  SB_CAT_ANNUAL_PHYSICAL_EXAM = "SB_CAT_ANNUAL_PHYSICAL_EXAM",
  SB_CAT_DEFINED_SUPPLEMENTAL_BENEFITS = "SB_CAT_DEFINED_SUPPLEMENTAL_BENEFITS",
  SB_CAT_REDUCED_COST_SHARING_FOR_VBID_UF_SSBCI = "SB_CAT_REDUCED_COST_SHARING_FOR_VBID_UF_SSBCI",
  SB_CAT_NON_PRIMARILY_HEALTH_RELATED_BENEFITS = "SB_CAT_NON_PRIMARILY_HEALTH_RELATED_BENEFITS",
  SB_CAT_HOME_HEALTH_SERVICES = "SB_CAT_HOME_HEALTH_SERVICES",
  SB_CAT_PROSTHETICS_MEDICAL_SUPPLIES = "SB_CAT_PROSTHETICS_MEDICAL_SUPPLIES",
  SB_CAT_ADDITIONAL_SERVICES = "SB_CAT_ADDITIONAL_SERVICES",
  SB_CAT_NON_OPIOID_PAIN_MANAGEMENT = "SB_CAT_NON_OPIOID_PAIN_MANAGEMENT",
  SB_CAT_DIALYSIS_SERVICES = "SB_CAT_DIALYSIS_SERVICES",
}

export interface PackageBenefit {
  readonly benefit_services: {
    readonly [x: string]: /* "SERVICE_PRIMARY_OR_SUCH" */ {
      readonly network_costs: NetworkCostDescriptors;
    };
  };
  readonly network_costs: NetworkCostDescriptors;
}

export interface PackageBenefits {
  readonly [x: string]: PackageBenefit;
}

export enum PackageServiceAvailability {
  ANY_DOCTOR = "AVAILABILITY_ANY_DOCTOR",
  ANY_WILLING_DOCTOR = "AVAILABILITY_ANY_WILLING_DOCTOR",
  INFORMATION_BEING_UPDATED = "AVAILABILITY_INFORMATION_BEING_UPDATED",
  INFORMATION_NOT_AVAILABLE = "AVAILABILITY_INFORMATION_NOT_AVAILABLE",
  NOT_PROVIDED = "AVAILABILITY_NOT_PROVIDED",
  NOT_SPECIFIED = "AVAILABILITY_NOT_SPECIFIED",
  PLAN_DOCTORS = "AVAILABILITY_PLAN_DOCTORS",
  PLAN_DOCTORS_MOST_SERVICES = "AVAILABILITY_PLAN_DOCTORS_MOST_SERVICES",
  PLAN_DOCTORS_SOME_SERVICES = "AVAILABILITY_PLAN_DOCTORS_SOME_SERVICES",
  PLAN_DOCTORS_WITH_EXCEPTIONS = "AVAILABILITY_PLAN_DOCTORS_WITH_EXCEPTIONS",
  PROVIDED = "AVAILABILITY_PROVIDED",
  PROVIDED_EXTRA_COST = "AVAILABILITY_PROVIDED_EXTRA_COST",
}

export interface PackageServices {
  readonly additional_physical_exams: string;
  readonly dental_services: string;
  readonly doctor_choice: string;
  readonly hearing_services: string;
  readonly monthly_plan_premium?: string;
  readonly outpatient_prescription: string;
  readonly vision_services: string;
}

export interface PDE {
  drug: PrescriptionDrug;
  pde_status: string;
  service_date?: string;
}

export interface Pharmacy {
  city: string;
  distance_miles?: number;
  in_network?: boolean;
  name: string;
  npi: string;
  phone: string;
  preferred?: boolean;
  state: string;
  street: string;
  unit: string;
  zipcode: string;
}

export interface PharmacyInfo {
  npi: string;
  name: string;
  in_network: boolean;
  mail_order: boolean;
  preferred: boolean;
  ltc: boolean;
}

export enum PharmacyFrequencies {
  days_30 = "days_30",
  days_90 = "days_90",
  days_any = "days_any",
}

export enum PharmacyPreferences {
  general = "general",
  preferred_mail_order = "preferred_mail_order",
  preferred_retail = "preferred_retail",
  standard_mail_order = "standard_mail_order",
  standard_retail = "standard_retail",
}

export enum PharmacyPreferencesAndFrequencies {
  PHARM_PRFD_FREQ_30_DAYS = "PHARM_PRFD_FREQ_30_DAYS",
  PHARM_PRFD_FREQ_90_DAYS = "PHARM_PRFD_FREQ_90_DAYS",
  PHARM_STD_FREQ_30_DAYS = "PHARM_STD_FREQ_30_DAYS",
  PHARM_STD_FREQ_90_DAYS = "PHARM_STD_FREQ_90_DAYS",
  PHARM_MAIL_PRFD_FREQ_30_DAYS = "PHARM_MAIL_PRFD_FREQ_30_DAYS",
  PHARM_MAIL_PRFD_FREQ_90_DAYS = "PHARM_MAIL_PRFD_FREQ_90_DAYS",
  PHARM_MAIL_STD_FREQ_30_DAYS = "PHARM_MAIL_STD_FREQ_30_DAYS",
  PHARM_MAIL_STD_FREQ_90_DAYS = "PHARM_MAIL_STD_FREQ_90_DAYS",
}

export interface PharmacyGeoPoint {
  type: "Feature";
  properties: Pharmacy;
  geometry: Point;
}

export interface PharmacyGeoPoints {
  type: "FeatureCollection";
  features: PharmacyGeoPoint[];
}

export interface PharmaciesResponse {
  geocode_error: string;
  pharmacies: PharmacyGeoPoints;
  request_id: string;
  source_address: PharmacyGeoPoint;
  source_location_type: string;
  total_results: number;
}

export interface InNetworkPharmaciesResponse extends PharmaciesResponse {
  mail_order_network: boolean;
  mail_order_preferred: boolean;
}

export enum PharmacyType {
  MAIL = "mail",
  MAIL_AND_RETAIL = "both",
  NONE = "",
  RETAIL = "retail",
}

export interface PhoneNumber {
  readonly extension: string;
  readonly number: string;
}

export interface Plan extends BasePlan {
  readonly abstract_benefits?: AbstractBenefits;
  readonly additional_supplemental_benefits: AdditionalSupplementalBenefits | null;
  readonly address_1: string;
  readonly address_2: string;
  readonly city: string;
  readonly cobrand_name: string;
  readonly drug_plan_stars: number;
  readonly drug_tiers: Record<string, unknown>;
  readonly federal_approval_status: string;
  readonly health_plan_stars: number;
  readonly member_phone: PhoneNumber | null;
  readonly member_tty: PhoneNumber | null;
  readonly network_size: string;
  readonly oopc: OOPCCategories;
  readonly optional_benefits: OptionalBenefitPackage[];
  readonly overall_stars: number;
  readonly package_benefits: PackageBenefits;
  readonly plan_note: string[];
  readonly previous_plans: [];
  readonly prospect_phone: PhoneNumber | null;
  readonly prospect_tty: PhoneNumber | null;
  readonly star_ratings: StarRating[];
  readonly state: string;
  readonly zipcode: string;
  readonly provider_network_web_address: string;
  readonly non_zero_dollar_dsnp: boolean;
}

export enum PlanCategoryType {
  CHRONIC_CARE = "PLAN_CATEGORY_CHRONIC_CARE",
  COST_1876 = "PLAN_CATEGORY_1876_COST",
  DEMONSTRATION_SNP = "PLAN_CATEGORY_DEMONSTRATION_SNP",
  EMPLOYER_PDP = "PLAN_CATEGORY_EMPLOYER_PDP",
  EMPLOYER_PFFS = "PLAN_CATEGORY_EMPLOYER_PFFS",
  EMPLOYER_PPO = "PLAN_CATEGORY_EMPLOYER_PPO",
  FALLBACK = "PLAN_CATEGORY_FALLBACK",
  HCPP_1833_COST = "PLAN_CATEGORY_HCPP_1833_COST",
  HMO = "PLAN_CATEGORY_HMO",
  HMOPOS = "PLAN_CATEGORY_HMOPOS",
  LOCAL_PPO = "PLAN_CATEGORY_LOCAL_PPO",
  MEDICARE_MEDICAID_PLAN = "PLAN_CATEGORY_MEDICARE_MEDICAID_PLAN",
  MEDICARE_PDP = "PLAN_CATEGORY_MEDICARE_PDP",
  MSA = "PLAN_CATEGORY_MSA",
  NA = "PLAN_CATEGORY_NA",
  NATIONAL_PACE = "PLAN_CATEGORY_NATIONAL_PACE",
  ORIGINAL_MEDICARE = "PLAN_CATEGORY_ORIGINAL_MEDICARE",
  PFFS = "PLAN_CATEGORY_PFFS",
  POS_CONTRACTOR = "PLAN_CATEGORY_POS_CONTRACTOR",
  PSO = "PLAN_CATEGORY_PSO",
  REGIONAL_PPO = "PLAN_CATEGORY_REGIONAL_PPO",
  RFB_HMO = "PLAN_CATEGORY_RFB_HMO",
  RFB_HMOPOS = "PLAN_CATEGORY_RFB_HMOPOS",
  RFB_LOCAL_PPO = "PLAN_CATEGORY_RFB_LOCAL_PPO",
  RFB_PFFS = "PLAN_CATEGORY_RFB_PFFS",
  RFB_PSO = "PLAN_CATEGORY_RFB_PSO",
  UNKNOWN = "PLAN_CATEGORY_UNKNOWN",
}

export interface PlanDataVersion {
  file: string;
  sha1_hash: string;
  file_date: string;
  metadata: string;
}

export enum PlanDetailsPageSection {
  OVERVIEW = "overview",
  BENEFITS_AND_COSTS = "benefits",
  DRUG_COVERAGE = "drug-coverage",
  EXTRA_BENEFITS = "extra-benefits",
  OPTIONAL_PACKAGES = "optional-packages",
  STAR_RATINGS = "star-ratings",
}

export enum PlanEnrollmentStatus {
  AVAILABLE = "ENROLLMENT_AVAILABLE",
  CONTACT = "ENROLLMENT_CONTACT_PLAN",
  NOT_NOW = "ENROLLMENT_NOT_AT_THIS_TIME",
}

export interface PlanInfoNeededForEnrollment {
  category: PlanCategoryType;
  contract_id: string;
  contract_year: string;
  name: string;
  plan_id: string;
  plan_type: PlanType;
  segment_id: string;
  snp_type: PlanSNPType;
}

export enum PlanMaterialLanguage {
  DEFAULT = "",
  SPANISH = "Spanish",
  OTHER = "Other",
}

export type PlanOrSRP = Plan | SearchResultPlan;

export interface PlanResults {
  readonly plans: SearchResultPlan[];
  readonly request_id: string;
  readonly search_summary: PlanResultsSearchSummary;
  readonly total_results: number;
}

export interface PlanResultsSearchSummary {
  categories: PlanCategoryFilters;
  dental: number;
  drugs: number;
  excluded: number;
  hearing: number;
  national: number;
  no_drugs: number;
  organization_names: string[];
  sanctioned: number;
  senior_savings_model: number;
  vision: number;
}

export interface PlanCategoryFilters {
  hmo: number;
  ppo: number;
  pffs: number;
  msa: number;
  cost: number;
}

export interface PlanSearch {
  [s: string]: unknown;
  readonly fips?: string;
  readonly title?: string;
  readonly year: string;
}

export enum PlanSNPType {
  SNP_TYPE_CHRONIC_OR_DISABLING = "SNP_TYPE_CHRONIC_OR_DISABLING",
  SNP_TYPE_DUAL_ELIGIBLE = "SNP_TYPE_DUAL_ELIGIBLE",
  SNP_TYPE_INSTITUTIONAL = "SNP_TYPE_INSTITUTIONAL",
  SNP_TYPE_NOT_SNP = "SNP_TYPE_NOT_SNP",
}

export enum PlanCardSNPTypeLabelKeys {
  SNP_TYPE_CHRONIC_OR_DISABLING = "plan_card.snp_type_chronic_condition",
  SNP_TYPE_DUAL_ELIGIBLE = "plan_card.snp_type_dual_eligible",
  SNP_TYPE_INSTITUTIONAL = "plan_card.snp_type_institutional",
}

export enum PlanType {
  MA = "PLAN_TYPE_MA",
  MAPD = "PLAN_TYPE_MAPD",
  MEDIGAP = "PLAN_TYPE_MEDIGAP",
  PDP = "PLAN_TYPE_PDP",
}

export enum PlanTypeLabels {
  "PLAN_TYPE_MA" = "medicare_advantage.label",
  "PLAN_TYPE_MAPD" = "medicare_advantage.label",
  "PLAN_TYPE_PDP" = "prescription_drug_plan.label",
}

export interface PrescriptionDrug {
  form: string;
  frequency: string;
  is_generic: boolean;
  name: string;
  ndc: string;
  package_quantity: string | number;
  package_size: string | number;
  package_type: string;
  package_units: string;
  quantity: number | string;
  rxcui: string;
  strength_units: string;
  strength: string;
  use_package_quantity: boolean;
}

export enum PrescriptionFrequency {
  DAYS_30 = "FREQUENCY_30_DAYS",
  DAYS_60 = "FREQUENCY_60_DAYS",
  DAYS_90 = "FREQUENCY_90_DAYS",
  DAYS_180 = "FREQUENCY_180_DAYS",
  DAYS_360 = "FREQUENCY_360_DAYS",
}

export interface Redaction {
  readonly issue: string;
  readonly type: string;
  readonly url: string;
}

export interface SearchResultPlan extends BasePlan {
  readonly annual_breakdown_total: number;
  readonly annual_drugs_total: number;
  readonly covered_drugs: number;
  readonly does_not_support_mail_order: boolean;
  readonly emergency_care_cost: string;
  readonly emergency_response_device: boolean;
  readonly home_safety_devices: boolean;
  readonly in_home_support: boolean;
  readonly maximum_oopc: string;
  readonly otc_drugs: boolean;
  readonly pharmacies: SearchResultPlanPharmacy[];
  readonly primary_doctor_visit_cost: string;
  readonly remaining_premium_and_drugs: number;
  readonly specialist_doctor_visit_cost: string;
  readonly telehealth: boolean;
  readonly remaining_premium_and_drugs_mail_order: number;
  readonly remaining_premium_and_drugs_retail: number;
  readonly transportation: boolean;
  readonly worldwide_emergency: boolean;
}

export interface SearchResultPlanPharmacy {
  readonly in_network: boolean;
  readonly npi: string;
}

export interface SearchResultsFilters {
  drugCoverageFilter?: DrugCoverageFilterOption;
  insuranceCarrierFilter: string;
  insuranceCarriers: string[];
  planCategoriesFilter: string[];
  planCoveragesFilter: string[];
  sortBy?: SortOptions;
  starRatingFilter: string;
}

export interface SepReason {
  code: string;
  date?: MCTDate;
  dateError?: string;
  key: string;
  requiresDate: boolean;
}

export interface SepReasonForApi {
  code: string;
  date: string;
}

export enum Sex {
  FEMALE = "female",
  MALE = "male",
  NONE = "",
}

export interface SlsxRedirectQueryParams {
  req_token: string;
  relay?: string;
}

export enum SortOptions {
  ANNUAL_DEDUCTIBLE = "ANNUAL_DEDUCTIBLE",
  ANNUAL_DRUGS_TOTAL = "ANNUAL_DRUGS_TOTAL",
  ANNUAL_TOTAL = "ANNUAL_TOTAL",
  DRUG_PLAN_DEDUCTIBLE = "DRUG_PLAN_DEDUCTIBLE",
  MONTHLY_PREMIUM = "MONTHLY_PREMIUM",
}

export interface StarRating {
  category: string;
  error: StarRatingError;
  rating: number;
}

export enum StarRatingError {
  STAR_ERROR_NO_DATA = "STAR_ERROR_NO_DATA",
  STAR_ERROR_NO_ERROR = "STAR_ERROR_NO_ERROR",
  STAR_ERROR_NOT_ENOUGH_DATA = "STAR_ERROR_NOT_ENOUGH_DATA",
  STAR_ERROR_NOT_OFFERED = "STAR_ERROR_NOT_OFFERED",
  STAR_ERROR_NOT_REQUIRED = "STAR_ERROR_NOT_REQUIRED",
  STAR_ERROR_PLAN_TOO_NEW = "STAR_ERROR_PLAN_TOO_NEW",
  STAR_ERROR_PLAN_TOO_SMALL = "STAR_ERROR_PLAN_TOO_SMALL",
}

export interface StringMap {
  [s: string]: string;
}

export enum Title {
  MISS = "Miss",
  MR = "Mr.",
  MRS = "Mrs.",
  MS = "Ms.",
  NONE = "",
}

export enum GetBenefitsFrom {
  WITHHOLD_NONE = "WITHHOLD_NONE",
  RRB = "WITHHOLD_RRB",
  SSI = "WITHHOLD_SSI",
}

export enum UserLanguage {
  ENGLISH = "en",
  SPANISH = "es",
}

export interface UtagModule {
  link: (obj: UtagEvent) => void;
}

export interface UtagEvent {
  event_action: string;
  event_category: "MCT";
  event_label: string;
}

export interface Window {
  PHRASEAPP_ENABLED: boolean;
}

export interface LocationWithNextRouteInState {
  nextRoute?: H.LocationDescriptorObject;
}
